import { useState, useEffect } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';

import { BlogCardProps } from 'src/types';
import { BrowserType, Pagination } from '@constants/common';

import { fetchBlogList, fetchTotalBlogCount } from '@lib/API/blogAPI';
import ShowMoreButton from '@components/showmore-button';
import BlogPostCard from '@components/blog-posts/blog-post-card';
import Breadcrumb from '@components/breadcrumbs';
import { generateBreadcrumbsFromUrl } from '@utils/common.utils';
import { PlatformType } from '@invideoio/web-shared/constants/common';

interface PaginatedBlogListingProps {
  listTypeKey?: string;
  listName: string;
  browserURL?: string;
  slug: string;
  browser: BrowserType;
  devicePlatform: PlatformType;
  trendingPosts: BlogCardProps[];
}

const PaginatedBlogListing = ({
  listTypeKey,
  listName,
  browserURL,
  trendingPosts,
  slug,
  browser,
  devicePlatform,
}: PaginatedBlogListingProps) => {
  const [paginatedList, setPaginatedList] = useState<BlogCardProps[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  async function loadMoreBlogs() {
    const payload = {
      listTypeKey,
      listTypeValue: slug,
      start: paginatedList.length,
      limit: Pagination.BLOGS_PER_PAGE,
    };
    const { blogList } = await fetchBlogList(payload);

    setPaginatedList(paginatedList => [...paginatedList, ...blogList]);
  }

  async function getBlogCount() {
    const { totalBlogs } = await fetchTotalBlogCount({
      listTypeKey,
      listTypeValue: slug,
    });
    setTotalCount(totalBlogs);
  }

  useEffect(() => {
    if (listTypeKey) {
      loadMoreBlogs();
      getBlogCount();
    }
  }, [listTypeKey]);

  return (
    <div className={ivclass('iv-container iv-mx-auto sm:iv-overflow-x-scroll')}>
      <div className={ivclass('iv-grid iv-grid-cols-12')}>
        <div
          className={ivclass(
            'iv-col-span-12',
            'iv-mx-[25px] md:iv-mx-[56px] sm:iv-mx-[20px]',
            'iv-mt-[72px] lg:iv-mt-[70px] md:iv-mt-[32px] sm:iv-mt-[26px]',
          )}
        >
          <div
            className={ivclass(
              'iv-px-[31px] lg:iv-px-[85px] md:iv-px-[0] sm:iv-px-[25px]',
              'iv-mt-20 lg:iv-mt-[22px]',
              'iv-mb-20 lg:iv-mb-[0]',
            )}
          >
            {trendingPosts?.length == 0 && (
              <div
                className={ivclass(
                  'iv-pt-15',
                  'iv-px-35 sm:iv-px-[0]',
                  'iv-mb-[27px]',
                )}
              >
                <Breadcrumb
                  breadcrumbItems={generateBreadcrumbsFromUrl(
                    browserURL?.replace(/.$/, '') || '',
                  ).filter(path => path.name !== 'profession')}
                />
              </div>
            )}
            <div
              className={ivclass(
                'iv-text-heading-2xl iv-font-extrabold',
                'md:iv-text-heading-m sm:iv-text-heading-xs',
                'sm:iv-leading-[42px]',
              )}
            >
              {totalCount} {`article${totalCount == 1 ? ' ' : 's '}`}
              <span className={ivclass('iv-heading-gradient')}>
                for {listName}
              </span>
            </div>
          </div>
        </div>

        <div
          className={ivclass(
            'iv-col-span-12',
            'iv-mx-auto',
            'iv-mt-[47px] lg:iv-mt-[70px] md:iv-mt-[15px] sm:iv-mt-[0]',
          )}
        >
          <div
            className={ivclass(
              'iv-grid iv-grid-cols-3 md:iv-grid-cols-2 sm:iv-grid-cols-1',
              'iv-col-span-12',
              'iv-w-[1240px] md:iv-w-[800px] sm:iv-w-[320px]',
              'iv-min-w-[fit-content]',
              'iv-mx-100 lg:iv-mx-[70px] md:iv-mx-25 sm:iv-mx-20',
              'iv-gap-x-50 lg:iv-gap-x-40 md:iv-gap-x-35 ',
              'iv-gap-y-40 md:iv-gap-y-25 sm:iv-gap-y-[18px]',
            )}
          >
            {paginatedList?.map(post => (
              <div key={post.id} className="iv-mx-auto">
                <BlogPostCard
                  {...post}
                  devicePlatform={devicePlatform}
                  browser={browser}
                  backgroundColorClass={'iv-bg-black-4'}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {!(totalCount == paginatedList.length) && (
        <div
          className={ivclass(
            'iv-mt-[76px] lg:iv-mt-[72px] md:iv-mt-50 sm:iv-mt-30',
          )}
        >
          <ShowMoreButton
            isPaginationEnded={totalCount == paginatedList.length}
            showMoreAction={loadMoreBlogs}
          />
        </div>
      )}
    </div>
  );
};

export default PaginatedBlogListing;
